import { useCallback, useEffect, useState } from 'react';
import { apiV2Instance } from '../../../common/lib/api-v2.client';

export interface LifetimeAccessStatus {
  /* eslint-disable camelcase */
  user_id: string;
  lifetime_pass_type: 'SINGLE' | 'DUAL' | 'NONE';
  single_pass_source?: 'none' | 'purchased' | 'gifted';
  upgrade_pass_source?: 'none' | 'purchased' | 'gifted';
  dual_pass_source?: 'none' | 'purchased' | 'gifted';
  lifetime_gift_single_available?: number;
  lifetime_gift_dual_available?: number;
  is_dual_member: boolean;
  single_activated_platform: string | null;
  effective_access: {
    iphone: boolean;
    android: boolean;
  };
  /* eslint-enable camelcase */
}

export interface LifetimeUpgradeOffer {
  bundleId: string;
  title: string;
  description: string;
  usdReferencePrice: number;
  isUpgration: boolean;
}

export interface LifetimeUpgradeOffers {
  /* eslint-disable camelcase */
  lifetime_pass_type: string;
  is_dual_member: boolean;
  eligible_for_upgrade: boolean;
  /* eslint-enable camelcase */
  offers: LifetimeUpgradeOffer[];
}

interface UseLifetimeAccessResult {
  status: LifetimeAccessStatus | null;
  offers: LifetimeUpgradeOffer[];
  eligibleForUpgrade: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useLifetimeAccess = (): UseLifetimeAccessResult => {
  const [status, setStatus] = useState<LifetimeAccessStatus | null>(null);
  const [offers, setOffers] = useState<LifetimeUpgradeOffer[]>([]);
  const [eligibleForUpgrade, setEligibleForUpgrade] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async (): Promise<LifetimeAccessStatus | null> => {
    const res = await apiV2Instance.get('/api/quickcam-direct-pay/lifetime-access/status');
    console.log('[useLifetimeAccess] status response:', JSON.stringify(res.data, null, 2));
    const body = res.data?.data;
    if (body?.lifetime_pass_type) {
      setStatus(body);
    }
    return body || null;
  }, []);

  const fetchOffers = useCallback(async () => {
    try {
      const res = await apiV2Instance.get('/api/quickcam-direct-pay/upgrade-offers');
      console.log('[useLifetimeAccess] upgrade-offers response:', JSON.stringify(res.data, null, 2));
      const body = res.data?.data;
      setOffers(Array.isArray(body?.offers) ? body.offers : []);
      setEligibleForUpgrade(Boolean(body?.eligible_for_upgrade));
    } catch (err: any) {
      console.warn('[useLifetimeAccess] upgrade-offers failed:', err?.message || err);
      setOffers([]);
      setEligibleForUpgrade(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const current = await fetchStatus();
      console.log(
        '[useLifetimeAccess] pass_type:',
        current?.lifetime_pass_type,
        '| is_dual_member:',
        current?.is_dual_member,
      );
      if (current?.lifetime_pass_type === 'SINGLE') {
        await fetchOffers();
      } else {
        setOffers([]);
        setEligibleForUpgrade(false);
      }
    } catch (err: any) {
      console.warn('[useLifetimeAccess] status failed:', err?.message || err);
      setError(err?.message || 'Failed to load lifetime access status.');
    } finally {
      setLoading(false);
    }
  }, [fetchStatus, fetchOffers]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { status, offers, eligibleForUpgrade, loading, error, refresh };
};
